import { useCallback, useState, type FormEvent } from 'react'
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Input, InlineError, PhoneInput } from '../../components/ui'
import { useToast } from '../../hooks/useToast'
import { useAppDispatch } from '../../store/hooks'
import { signedIn } from '../../features/auth/authSlice'
import {
  useRegisterMutation,
  useSendSignupOtpMutation,
  useVerifySignupOtpMutation,
} from '../../services/authApi'
import { AuthCard, AuthColumn } from './AuthCard'
import { OtpInput } from './OtpInput'
import { PasswordInput } from './PasswordInput'
import { isNormalizedError } from '../../services/errorNormalizer'
import { isSendableMobile, maskMobile, SENDABLE_MOBILE_MESSAGE, toE164 } from '../../utils/phone'
import { safeNext } from './returnTo'
import { RESEND_SECONDS, useResendCountdown } from './useResendCountdown'
import { useSession } from './useSession'

const MIN_PASSWORD = 8

interface SignUpState {
  mobile: string
  signupToken: string
  /** Server-reported cooldown at the moment the code was sent. */
  resendIn: number
}

/**
 * H4 — `/{slug}/signup/*`: phone → OTP → password → done.
 *
 * The three steps share one piece of state held here rather than in the URL: a
 * verified `signup_token` is a credential and has no business being pasted into a
 * chat. A reload therefore drops the customer back at step one, which is also what
 * the backend wants — the ticket only lives 30 minutes.
 */
export function SignUpRoutes() {
  const { slug, isAuthenticated } = useSession()
  const { pathname } = useLocation()
  const [params] = useSearchParams()
  const [state, setState] = useState<SignUpState>({ mobile: '', signupToken: '', resendIn: RESEND_SECONDS })

  // Read once, on the step the customer arrived at: the navigations between
  // sign-up steps carry no query string, so anything left in the URL would be
  // gone by the time the confirmation needs it.
  const [next] = useState(() => safeNext(params.get('next'), slug))

  // Registering signs the customer in, so the last step is reached *while*
  // authenticated. Sending every signed-in visitor away would therefore bounce
  // them straight past their own confirmation — the guard is for someone who
  // wanders into sign-up already having an account.
  const onConfirmation = pathname.endsWith('/signup/done')
  if (isAuthenticated && !onConfirmation) return <Navigate to={next} replace />

  return (
    <Routes>
      <Route
        index
        element={<PhoneStep slug={slug} next={next} onSent={(mobile, resendIn) => setState({ mobile, signupToken: '', resendIn })} />}
      />
      <Route
        path="verify"
        element={
          <VerifyStep
            slug={slug}
            mobile={state.mobile}
            resendIn={state.resendIn}
            onVerified={(signupToken) => setState((s) => ({ ...s, signupToken }))}
          />
        }
      />
      <Route path="password" element={<PasswordStep slug={slug} state={state} />} />
      <Route path="done" element={<DoneStep slug={slug} next={next} />} />
      <Route path="*" element={<Navigate to={`/${slug}/signup`} replace />} />
    </Routes>
  )
}

/* ------------------------------------------------------------------ step 1 */

function PhoneStep({ slug, next, onSent }: { slug: string; next: string; onSent: (mobile: string, resendIn: number) => void }) {
  const navigate = useNavigate()
  const toast = useToast()
  const [mobile, setMobile] = useState('')
  const [touched, setTouched] = useState(false)
  const [sendOtp, { isLoading, error }] = useSendSignupOtpMutation()

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setTouched(true)
    if (!isSendableMobile(mobile)) return

    const normalized = toE164(mobile)
    const result = await sendOtp({ slug, mobile: normalized })
    if (!('data' in result)) return

    onSent(normalized, result.data?.resend_in ?? RESEND_SECONDS)
    // Dev builds echo the code back because no SMS gateway is wired yet.
    if (result.data?.otp) toast.show(`Verification code: ${result.data.otp}`, { duration: 0 })
    navigate(`/${slug}/signup/verify`)
  }

  return (
    <AuthColumn
      title="What's your number?"
      subtitle="We'll send a 6-digit code to verify your mobile device."
      footer={
        <>
          Already have an account?{' '}
          <Link
            to={`/${slug}/signin?next=${encodeURIComponent(next)}`}
            className="ml-1 font-label-caps text-label-caps uppercase text-primary transition-colors hover:text-primary-container"
          >
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} noValidate className="space-y-8">
        {error && <InlineError error={error} />}
        <PhoneInput
          label="Phone number"
          value={mobile}
          onChange={setMobile}
          error={touched && !isSendableMobile(mobile) ? SENDABLE_MOBILE_MESSAGE : undefined}
          required
        />
        <Button type="submit" fullWidth size="lg" loading={isLoading}>
          Send code
        </Button>
      </form>
    </AuthColumn>
  )
}

/* ------------------------------------------------------------------ step 2 */

function VerifyStep({
  slug,
  mobile,
  resendIn,
  onVerified,
}: {
  slug: string
  mobile: string
  resendIn: number
  onVerified: (signupToken: string) => void
}) {
  const navigate = useNavigate()
  const toast = useToast()
  const [otp, setOtp] = useState('')
  const [verify, { isLoading, error: verifyError, reset: clearVerify }] = useVerifySignupOtpMutation()
  const [resend, { isLoading: resending, error: resendError, reset: clearResend }] = useSendSignupOtpMutation()
  const [seconds, setSeconds] = useResendCountdown(resendIn)
  const error = resendError ?? verifyError

  const submit = useCallback(
    async (code: string) => {
      if (code.length !== 6) return
      clearResend()
      const result = await verify({ slug, mobile, otp: code })
      if (!('data' in result) || !result.data) return
      onVerified(result.data.signup_token)
      navigate(`/${slug}/signup/password`)
    },
    [verify, clearResend, slug, mobile, onVerified, navigate],
  )

  if (!mobile) return <Navigate to={`/${slug}/signup`} replace />

  // OC-427: a refused resend used to vanish silently. Its error is shown now, and the
  // countdown follows the cooldown the server reports either way.
  const resendCode = async () => {
    clearVerify()
    const result = await resend({ slug, mobile })
    if ('error' in result) {
      if (isNormalizedError(result.error) && result.error.retryAfter) setSeconds(result.error.retryAfter)
      return
    }
    setOtp('')
    setSeconds(result.data?.resend_in ?? RESEND_SECONDS)
    if (result.data?.otp) toast.show(`Verification code: ${result.data.otp}`, { duration: 0 })
    else toast.success('We sent a new code.')
  }

  return (
    <AuthCard
      icon="smartphone"
      title="Check your phone"
      subtitle={
        <>
          We&apos;ve sent a 6-digit verification code to{' '}
          <strong className="font-medium text-on-surface">{maskMobile(mobile)}</strong>. Enter it below to confirm
          your identity.
        </>
      }
      footer={
        <Link
          to={`/${slug}/signup`}
          className="inline-flex items-center gap-2 font-label-caps text-label-caps uppercase text-on-surface-variant transition-colors hover:text-primary"
        >
          <span aria-hidden="true" className="material-symbols-outlined text-[16px]">
            arrow_back
          </span>
          Use a different number
        </Link>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void submit(otp)
        }}
        className="space-y-8"
      >
        {error && <InlineError error={error} />}
        <OtpInput value={otp} onChange={setOtp} onComplete={(code) => void submit(code)} error={Boolean(verifyError)} disabled={isLoading} />
        <Button type="submit" fullWidth size="lg" loading={isLoading} disabled={otp.length !== 6}>
          Verify and continue
        </Button>
        <p className="text-center font-body-md text-body-md text-on-surface-variant">
          {seconds > 0 ? (
            <>Didn&apos;t receive the code? Resend in {seconds}s</>
          ) : (
            <>
              Didn&apos;t receive the code?{' '}
              <Button variant="text" size="sm" onClick={() => void resendCode()} loading={resending}>
                Resend code
              </Button>
            </>
          )}
        </p>
      </form>
    </AuthCard>
  )
}

/* ------------------------------------------------------------------ step 3 */

function PasswordStep({ slug, state }: { slug: string; state: SignUpState }) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [touched, setTouched] = useState(false)
  const [register, { isLoading, error }] = useRegisterMutation()

  if (!state.signupToken) return <Navigate to={`/${slug}/signup`} replace />

  const nameError = touched && !firstName.trim() ? 'Enter your first name.' : undefined
  const passwordError =
    touched && password.length < MIN_PASSWORD ? `Use at least ${MIN_PASSWORD} characters.` : undefined
  const confirmError = touched && confirm !== password ? 'These passwords don’t match.' : undefined

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setTouched(true)
    if (!firstName.trim() || password.length < MIN_PASSWORD || confirm !== password) return

    const result = await register({
      slug,
      signupToken: state.signupToken,
      mobile: state.mobile,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim() || null,
      password,
    })
    if (!('data' in result) || !result.data) return

    dispatch(
      signedIn({
        slug,
        token: result.data.token,
        expiresIn: result.data.expires_in ?? undefined,
        customer: result.data.customer,
      }),
    )
    navigate(`/${slug}/signup/done`, { replace: true })
  }

  return (
    <AuthCard
      width="xl"
      title="Create a password"
      subtitle="Last step — tell us who you are and choose a strong password to protect your account."
    >
      <form onSubmit={submit} noValidate className="space-y-6">
        {error && <InlineError error={error} />}

        <div className="grid gap-6 sm:grid-cols-2">
          <Input
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            error={nameError}
            autoComplete="given-name"
            required
          />
          <Input
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
          />
        </div>

        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          hint="Optional. We'll send your appointment confirmations here."
        />

        <PasswordInput
          label="Password"
          value={password}
          onChange={setPassword}
          error={passwordError}
          autoComplete="new-password"
          hint={`At least ${MIN_PASSWORD} characters.`}
          required
        />

        <PasswordInput
          label="Confirm password"
          value={confirm}
          onChange={setConfirm}
          error={confirmError}
          autoComplete="new-password"
          required
        />

        <div className="pt-2">
          <Button type="submit" fullWidth size="lg" loading={isLoading}>
            Create account
          </Button>
        </div>
      </form>
    </AuthCard>
  )
}

/* ------------------------------------------------------------------ step 4 */

function DoneStep({ slug, next }: { slug: string; next: string }) {
  // A customer who came from the wizard is mid-booking with a slot held in
  // sessionStorage, so the way onward is back into it, not a fresh one.
  const midBooking = next.startsWith(`/${slug}/book`)

  return (
    <AuthCard
      icon="check_circle"
      iconStyle="tile"
      title="Account created"
      subtitle={
        midBooking
          ? "Welcome. Your appointment is still waiting — let's finish it."
          : "Welcome. You're signed in and ready to book."
      }
    >
      <div className="flex flex-col gap-4">
        <Button to={midBooking ? next : `/${slug}/book`} fullWidth size="lg">
          {midBooking ? 'Finish my booking' : 'Book an appointment'}
        </Button>
        <Button to={`/${slug}/account`} variant="outlined" fullWidth size="lg">
          Go to my account
        </Button>
      </div>
    </AuthCard>
  )
}
